import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import {
  BuildingIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  FolderUpIcon,
  HomeIcon,
  ImagesIcon,
  LayoutDashboardIcon,
  Settings,
  Sun,
} from 'lucide-react';

import { useMe } from '@/api/me';
import { SidebarLink } from '@/components/ui/sidebar-link';
import { currentTenantAtom } from '@/constants/currrentTenant';
import { env } from '@/constants/env';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';
import { parseStringToBool } from '@/utils/parseStringToBool';

type SidebarProps = {
  isVisible: boolean;
  closeMobileSidebarMenu: () => void;
};

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

export const Sidebar = ({ isVisible, closeMobileSidebarMenu }: SidebarProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isMobileView = !useResizeObserver(768);
  const currentTenant = useAtomValue(currentTenantAtom);
  const me = useMe({
    tenantId: currentTenant?.id,
  });

  const [isSidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    parseStringToBool(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) || 'false')
  );

  const isCollapsed = isSidebarCollapsed && !isMobileView;

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const nextVal = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, nextVal ? 'true' : 'false');
      return nextVal;
    });
  };

  const handleClick = (ev: EventTarget) => {
    if (isMobileView && !(ev as HTMLElement).closest('.ignore-click')) {
      closeMobileSidebarMenu();
    }
  };

  return (
    <aside
      className={cn(
        isVisible
          ? 'top-[60px] ease-linear max-md:left-0 max-md:w-full max-md:translate-x-0'
          : 'max-lg:absolute max-lg:left-0 max-lg:-translate-x-full',
        'z-50 h-[calc(100vh-60px)] w-full flex-col bg-transparent pb-6 transition-all duration-200 max-md:absolute max-md:bg-white max-md:p-4',
        isCollapsed ? 'max-w-[42px]' : 'max-w-[230px] max-md:max-w-[270px]'
      )}
    >
      <nav
        className="flex flex-col gap-1"
        onClick={(ev) => handleClick(ev.target)}
        onKeyUp={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e.target);
          }
        }}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            'ignore-click rounded-md hover:bg-primary-darker/10 max-md:hidden [&_svg]:size-6 [&_svg]:shrink-0 [&_svg]:text-primary',
            isCollapsed ? 'px-2' : 'px-6 max-md:px-2'
          )}
        >
          {isCollapsed ? <ChevronsRightIcon size={20} /> : <ChevronsLeftIcon size={20} />}
        </button>
        <SidebarLink
          to="/"
          label={t('dashboard')}
          icon={<LayoutDashboardIcon />}
          isSidebarCollapsed={isCollapsed}
        />
        <SidebarLink
          to="/estates"
          icon={<HomeIcon />}
          label={t('estates')}
          isSidebarCollapsed={isCollapsed}
        />
        {!isSidebarCollapsed &&
        (pathname.startsWith('/estates') || pathname.startsWith('/projects')) ? (
          <>
            <SidebarLink
              to="/estates/me"
              className="ml-5"
              icon={<FolderUpIcon />}
              label={t('myEstates')}
              inactiveProps={{ className: 'ml-5' }}
            />
            <SidebarLink
              to="/projects"
              className="ml-5"
              icon={<BuildingIcon />}
              label={t('projects')}
              inactiveProps={{ className: 'ml-5' }}
            />
          </>
        ) : null}
        {parseStringToBool(env.IMAGE_RENDERING) ? (
          <SidebarLink
            to="/image-rendering"
            icon={<ImagesIcon />}
            label={t('rendering')}
            isSidebarCollapsed={isCollapsed}
          />
        ) : null}
        {me.data.tenantEmployees[0]?.role === 'admin' ? (
          <SidebarLink
            to="/tenant/$tenantId/users"
            params={{ tenantId: currentTenant?.id }}
            icon={<Settings />}
            label={t('setup')}
            isSidebarCollapsed={isCollapsed}
          />
        ) : null}
        <SidebarLink
          to="/shadowmap"
          icon={<Sun />}
          label={t('shadowmap-simulation')}
          isSidebarCollapsed={isCollapsed}
        />
      </nav>
    </aside>
  );
};
